import { useRef, useState } from "react";
import styled, { keyframes } from "styled-components";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { logoutAction, profileState } from "../../app/reducers/profile-slice";
import ProfileManagerIcon from "../icons/ProfileManagerIcon/ProfileManagerIcon";
import theme from "../../style/theme";
import { clearRoutingSummaryAction, routingState } from "../../app/reducers/routing-slice";
import { formatNumberWithCommas } from "../../utils/number/format-number";
import { setRoutingSummaryDispatch } from "../../app/dispatch/routing/set-routing-summary";
import CircularProgress from "@mui/material/CircularProgress";
import { getUserTypeLabel } from "../../types/user/user-type";

export const ProfileMenu = () => {
  const dispatch = useAppDispatch();
  const { profile } = useAppSelector(profileState);
  const { monthlyLogs, loading } = useAppSelector(routingState);
  const ref = useRef<HTMLDivElement>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const isMobile = window.innerWidth <= 768;

  const handleClickLogoutButton = () => {
    dispatch(clearRoutingSummaryAction());
    dispatch(logoutAction());
    setMenuOpen(false);
  };

  const handleClickRoutingSummaryButton = () => {
    dispatch(setRoutingSummaryDispatch());
  };

  const toggleMenu = () => {
    setMenuOpen(!menuOpen);
  };

  return (
    <MenuContainer ref={ref}>
      <ProfileButton onClick={toggleMenu}>
        <ProfileManagerIcon width={18} height={18} />
        <ProfileTextContent>{profile?.name}</ProfileTextContent>
        <ExpandIcon $menuOpen={menuOpen}>▾</ExpandIcon>
      </ProfileButton>
      <ProfileMenuContent className="profile-menu-content" $menuOpen={menuOpen}>
        <MenuInner>
          <MenuHeader>
            <MenuTitle>{profile?.name} {getUserTypeLabel(profile?.userType)}님</MenuTitle>
            <MenuDivider />
          </MenuHeader>
          <MenuGrid>
            <MenuButton onClick={handleClickLogoutButton}>
              <MenuItemContent>
                <MenuItemTitle>로그아웃</MenuItemTitle>
                <MenuItemIcon>→</MenuItemIcon>
              </MenuItemContent>
            </MenuButton>
            <MenuButton onClick={handleClickRoutingSummaryButton}>
              <MenuItemContent>
                <MenuItemTitle>
                  {loading ?
                    <CircularProgress sx={{ color: theme.palette.primitives.green400 }} size={18} /> :
                    '동선 최적화 사용량'}
                </MenuItemTitle>
                <MenuItemIcon>→</MenuItemIcon>
              </MenuItemContent>
            </MenuButton>
          </MenuGrid>
        </MenuInner>

        {(monthlyLogs !== undefined && !loading) && (
          <RoutingSummaryResultContainer>
            <UsageSection>
              <UsageSectionTitle>이번 달 이용내역</UsageSectionTitle>
              <UsageRow>
                <UsageLabel>청구액</UsageLabel>
                <UsageValue>{formatNumberWithCommas(monthlyLogs.current.estimatedCharge)}원</UsageValue>
              </UsageRow>
              <UsageRow>
                <UsageLabel>최적화 횟수</UsageLabel>
                <UsageValue>{formatNumberWithCommas(monthlyLogs.current.numRequests)}회</UsageValue>
              </UsageRow>
            </UsageSection>

            <UsageSection>
              <UsageSectionTitle>지난 달 이용내역</UsageSectionTitle>
              <UsageRow>
                <UsageLabel>청구액</UsageLabel>
                <UsageValue>{formatNumberWithCommas(monthlyLogs.last.estimatedCharge)}원</UsageValue>
              </UsageRow>
              <UsageRow>
                <UsageLabel>최적화 횟수</UsageLabel>
                <UsageValue>{formatNumberWithCommas(monthlyLogs.last.numRequests)}회</UsageValue>
              </UsageRow>
            </UsageSection>

            <UpdateTime>업데이트: {monthlyLogs.formattedUpdateTime}</UpdateTime>
          </RoutingSummaryResultContainer>
        )}
      </ProfileMenuContent>
    </MenuContainer>
  );
};

const slideInAnimation = keyframes`
  0% {
    opacity: 0;
    transform: translateY(-8px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
`;

const MenuContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  
  &:hover .profile-menu-content {
    display: block;
  }

  @media (max-width: 768px) {
    width: 100%;
    margin: 12px 0;
    padding: 0;
    flex-direction: column;
    align-items: stretch;
  }
`;

const ProfileButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  height: 36px;
  padding: 0 12px;
  margin: 0 12px;
  background-color: rgba(255, 255, 255, 0.05);
  border: solid 1px rgba(255, 255, 255, 0.1);
  border-radius: 100px;
  cursor: pointer;
  color: ${theme.palette.primitives.gray100};
  transition: all 0.2s ease;
  
  @media (max-width: 768px) {
    width: 100%;
    margin: 0;
    padding: 0 24px;
    justify-content: center;
    height: 44px;
  }
  
  &:hover {
    background-color: rgba(252, 202, 0, 0.1);
    border-color: rgba(252, 202, 0, 0.2);
  }
`;

const ProfileTextContent = styled.div`
  font-weight: 500;
  font-size: 13px;
  line-height: 17px;
  color: ${theme.palette.primitives.gray100};
  
  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

const ExpandIcon = styled.span<{ $menuOpen: boolean }>`
  font-size: 12px;
  line-height: 1;
  color: rgba(255, 255, 255, 0.6);
  transition: transform 0.2s ease;
  transform: ${props => props.$menuOpen ? 'rotate(180deg)' : 'none'};
  
  ${MenuContainer}:hover & {
    color: #fcca00;
  }
`;

const ProfileMenuContent = styled.div<{ $menuOpen: boolean }>`
  position: absolute;
  top: 48px;
  right: 0;
  width: 220px;
  background: rgba(28, 32, 38, 0.98);
  backdrop-filter: blur(8px);
  border-radius: 8px;
  z-index: 1000000;
  display: ${props => props.$menuOpen ? 'block' : 'none'};
  animation: ${slideInAnimation} 0.2s ease-out;
  box-shadow: 
    0 4px 12px rgba(0, 0, 0, 0.15),
    0 0 0 1px rgba(255, 255, 255, 0.1);
  overflow: hidden;

  @media (max-width: 768px) {
    position: relative;
    top: 0;
    left: 0;
    right: 0;
    width: 100%;
    margin-top: 8px;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    max-height: none;
    padding: 0 12px;
    box-sizing: border-box;
  }
`;

const MenuInner = styled.div`
  padding: 8px;
  
  @media (max-width: 768px) {
    padding: 8px 4px;
  }
`;

const MenuHeader = styled.div`
  margin-bottom: 8px;
  text-align: center;
`;

const MenuTitle = styled.h3`
  color: #fcca00;
  margin: 0;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.5px;
  position: relative;
  display: inline-block;
`;

const MenuDivider = styled.div`
  height: 1px;
  background: linear-gradient(
    to right,
    rgba(252, 202, 0, 0.3),
    rgba(252, 202, 0, 0.1)
  );
  margin-top: 8px;
`;

const MenuGrid = styled.div`
  display: grid;
  gap: 4px;
  
  @media (max-width: 768px) {
    padding: 0 4px;
  }
`;

const MenuButton = styled.button`
  width: 100%;
  padding: 8px 12px;
  background-color: transparent;
  font-size: 0.875rem;
  font-weight: 500;
  color: #ffffff;
  border: none;
  cursor: pointer;
  border-radius: 6px;
  transition: all 0.15s ease;
  text-align: left;

  @media (max-width: 768px) {
    padding: 12px 8px;
    font-size: 1rem;
  }

  &:hover {
    background: rgba(252, 202, 0, 0.1);
  }
`;

const MenuItemContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 4px;
`;

const MenuItemTitle = styled.span`
  color: #ffffff;
  font-weight: 500;
  display: flex;
  align-items: center;
`;

const MenuItemIcon = styled.span`
  color: #fcca00;
  opacity: 0;
  transform: translateX(-4px);
  transition: all 0.2s ease;

  ${MenuButton}:hover & {
    opacity: 1;
    transform: translateX(0);
  }
`;

const RoutingSummaryResultContainer = styled.div`
  background: rgba(20, 25, 31, 0.95);
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  padding: 12px;
  
  @media (max-width: 768px) {
    padding: 12px 16px;
  }
`;

const UsageSection = styled.div`
  margin-bottom: 12px;
`;

const UsageSectionTitle = styled.h4`
  color: rgba(252, 202, 0, 0.8);
  font-size: 12px;
  font-weight: 500;
  margin: 0 0 6px 0;
`;

const UsageRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
`;

const UsageLabel = styled.span`
  color: rgba(255, 255, 255, 0.6);
  font-size: 12px;
`;

const UsageValue = styled.span`
  color: white;
  font-size: 12px;
  font-weight: 500;
`;

const UpdateTime = styled.div`
  color: rgba(255, 255, 255, 0.4);
  font-size: 10px;
  text-align: right;
  margin-top: 4px;
`;
